import React, { useState, useEffect } from 'react';
import { getPromptConfig, updatePrompt, PromptConfig, UpdatePromptRequest } from '../services/api';
import './PromptSettings.css';

interface PromptSettingsProps {
  onClose: () => void;
}

const PromptSettings: React.FC<PromptSettingsProps> = ({ onClose }) => {
  const [config, setConfig] = useState<PromptConfig | null>(null);
  const [requiredTags, setRequiredTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    provider: 'openai' | 'perplexity';
    type: 'systemPrompt' | 'userPrompt';
  } | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPromptConfig();
      setConfig(response.config);
      setRequiredTags(response.requiredTags);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load prompt configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    provider: 'openai' | 'perplexity',
    type: 'systemPrompt' | 'userPrompt',
    prompt: string
  ) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updateData: UpdatePromptRequest = {
        provider,
        type,
        prompt,
      };

      const response = await updatePrompt(updateData);

      if (response.success) {
        setSuccess('Prompt updated successfully!');
        if (response.config) {
          setConfig(response.config);
        }
        setEditing(null);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to update prompt');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to update prompt. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (
    provider: 'openai' | 'perplexity',
    type: 'systemPrompt' | 'userPrompt'
  ) => {
    setEditing({ provider, type });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditing(null);
    setError(null);
    setSuccess(null);
  };

  const validatePrompt = (prompt: string, type: 'systemPrompt' | 'userPrompt'): string | null => {
    if (type === 'userPrompt') {
      const missingTags = requiredTags.filter((tag) => !prompt.includes(tag));
      if (missingTags.length > 0) {
        return `Missing required replacement tags: ${missingTags.join(', ')}`;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="prompt-settings-overlay">
        <div className="prompt-settings-modal">
          <div className="loading">Loading prompt configuration...</div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="prompt-settings-overlay">
        <div className="prompt-settings-modal">
          <div className="error">Failed to load prompt configuration</div>
          <button onClick={onClose} className="close-button">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-settings-overlay" onClick={onClose}>
      <div className="prompt-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="prompt-settings-header">
          <h2>System Prompt Configuration</h2>
          <button onClick={onClose} className="close-button" aria-label="Close">
            ×
          </button>
        </div>

        <div className="prompt-settings-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="required-tags-info">
            <h3>Required Replacement Tags (must be preserved in User Prompts):</h3>
            <div className="tags-list">
              {requiredTags.map((tag) => (
                <span key={tag} className="tag-badge">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {(['openai', 'perplexity'] as const).map((provider) => (
            <div key={provider} className="provider-section">
              <h3 className="provider-title">
                {provider === 'openai' ? 'OpenAI' : 'Perplexity'}
              </h3>

              <div className="prompt-section">
                <div className="prompt-header">
                  <label>System Prompt</label>
                  {editing?.provider === provider &&
                  editing?.type === 'systemPrompt' ? (
                    <div className="edit-actions">
                      <button
                        onClick={handleCancel}
                        className="cancel-button"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(provider, 'systemPrompt')}
                      className="edit-button"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {editing?.provider === provider &&
                editing?.type === 'systemPrompt' ? (
                  <PromptEditor
                    prompt={config[provider].systemPrompt}
                    onSave={(newPrompt) =>
                      handleSave(provider, 'systemPrompt', newPrompt)
                    }
                    onCancel={handleCancel}
                    saving={saving}
                    validate={(p) => validatePrompt(p, 'systemPrompt')}
                  />
                ) : (
                  <div className="prompt-display">
                    <pre>{config[provider].systemPrompt}</pre>
                  </div>
                )}
              </div>

              <div className="prompt-section">
                <div className="prompt-header">
                  <label>User Prompt</label>
                  {editing?.provider === provider &&
                  editing?.type === 'userPrompt' ? (
                    <div className="edit-actions">
                      <button
                        onClick={handleCancel}
                        className="cancel-button"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(provider, 'userPrompt')}
                      className="edit-button"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {editing?.provider === provider &&
                editing?.type === 'userPrompt' ? (
                  <PromptEditor
                    prompt={config[provider].userPrompt}
                    onSave={(newPrompt) =>
                      handleSave(provider, 'userPrompt', newPrompt)
                    }
                    onCancel={handleCancel}
                    saving={saving}
                    validate={(p) => validatePrompt(p, 'userPrompt')}
                    requiredTags={requiredTags}
                  />
                ) : (
                  <div className="prompt-display">
                    <pre>{config[provider].userPrompt}</pre>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface PromptEditorProps {
  prompt: string;
  onSave: (newPrompt: string) => void;
  onCancel: () => void;
  saving: boolean;
  validate: (prompt: string) => string | null;
  requiredTags?: string[];
}

const PromptEditor: React.FC<PromptEditorProps> = ({
  prompt,
  onSave,
  onCancel,
  saving,
  validate,
  requiredTags = [],
}) => {
  const [editedPrompt, setEditedPrompt] = useState(prompt);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = () => {
    const error = validate(editedPrompt);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    onSave(editedPrompt);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedPrompt(e.target.value);
    setValidationError(null);
  };

  return (
    <div className="prompt-editor">
      <textarea
        value={editedPrompt}
        onChange={handleChange}
        className="prompt-textarea"
        rows={15}
        placeholder="Enter your prompt here..."
      />
      {validationError && (
        <div className="validation-error">{validationError}</div>
      )}
      {requiredTags.length > 0 && (
        <div className="tags-reminder">
          <strong>Remember to include these tags:</strong>
          <div className="tags-list">
            {requiredTags.map((tag) => (
              <span
                key={tag}
                className={`tag-badge ${
                  editedPrompt.includes(tag) ? 'tag-present' : 'tag-missing'
                }`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="editor-actions">
        <button
          onClick={handleSave}
          className="save-button"
          disabled={saving || !!validationError}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="cancel-button"
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PromptSettings;

